import Image from 'next/image'
import { FunctionComponent, useEffect } from 'react'
import styled from 'styled-components'
import type { OurIntlShape } from '@/i18n/typing'
import PlatformImage from '@/utils/export-pptx/components/platform-image'
import {
  SlideContainer,
  SlideTitle,
  Card,
  CardTitle,
} from '@/utils/export-pptx/components/slides/styles'
import { chartSize, PptxObjectType } from '@/utils/export-pptx/constants'
import useWordCloudDataUri from '@/utils/export-pptx/hooks/use-word-cloud-data-uri'
import type { CommentWordCloudSlide } from '@/utils/export-pptx/types/slide'

interface SlideProps {
  slide: CommentWordCloudSlide
  onSlideReady?: (slide: CommentWordCloudSlide) => void
  formatMessage: OurIntlShape['formatMessage']
}

const SlideComponent: FunctionComponent<SlideProps> = ({
  slide,
  onSlideReady,
  formatMessage,
}) => {
  const dataUri = useWordCloudDataUri({
    words: slide.payload.words,
  })

  useEffect(() => {
    if (dataUri === undefined) {
      return
    }

    onSlideReady?.(slide)
  }, [dataUri, onSlideReady, slide])

  return (
    <SlideContainer data-pptx-object={PptxObjectType.Shape}>
      <SlideTitle data-pptx-object={PptxObjectType.Shape} mb={12}>
        <PlatformImage
          height={54}
          platform={slide.payload.platform}
          width={54}
        />
        <span data-pptx-object={PptxObjectType.Text}>
          {formatMessage({ id: 'autoreport:analysis_section_comment' })}
        </span>
      </SlideTitle>

      <Card data-pptx-object={PptxObjectType.Shape}>
        <CardTitle data-pptx-object={PptxObjectType.Text}>
          {slide.payload.title}
        </CardTitle>

        <CardContent>
          <WordCloudContainer>
            {dataUri && (
              <Image
                data-original-size
                alt='comment-word-cloud'
                data-pptx-object={PptxObjectType.Image}
                height={chartSize.wordCloud.h}
                src={dataUri}
                width={chartSize.wordCloud.w}
              />
            )}
          </WordCloudContainer>

          {slide.payload.words.length > 0 && (
            <WordCloudListContainer>
              <WordCloudListItem>
                <WordCloudListItemHeader data-pptx-object={PptxObjectType.Text}>
                  {formatMessage({ id: 'autoreport:chart_field_order' })}
                </WordCloudListItemHeader>
                <WordCloudListItemHeader data-pptx-object={PptxObjectType.Text}>
                  {formatMessage({ id: 'autoreport:chart_field_keyword' })}
                </WordCloudListItemHeader>
                <WordCloudListItemHeader data-pptx-object={PptxObjectType.Text}>
                  {formatMessage({ id: 'autoreport:chart_field_keyword_freq' })}
                </WordCloudListItemHeader>
              </WordCloudListItem>

              {slide.payload.words.slice(0, 5).map((item, index) => (
                <WordCloudListItem key={item.text}>
                  <WordCloudListItemBorderTop
                    data-pptx-object={PptxObjectType.Shape}
                  />

                  <WordCloudListItemColumn
                    data-pptx-object={PptxObjectType.Text}
                  >
                    {index + 1}
                  </WordCloudListItemColumn>
                  <WordCloudListItemColumn
                    data-pptx-object={PptxObjectType.Text}
                  >
                    {item.text}
                  </WordCloudListItemColumn>

                  <WordCloudListItemColumn
                    data-pptx-object={PptxObjectType.Text}
                  >
                    {item.value}
                  </WordCloudListItemColumn>
                </WordCloudListItem>
              ))}
            </WordCloudListContainer>
          )}
        </CardContent>

        {slide.payload.words.length > 0 && slide.payload.aiSummary && (
          <Summary data-pptx-object={PptxObjectType.Text}>
            {slide.payload.aiSummary}
          </Summary>
        )}
      </Card>
    </SlideContainer>
  )
}

const CardContent = styled.div`
  display: grid;
  align-items: center;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;
`

const WordCloudContainer = styled.div`
  height: ${chartSize.wordCloud.h}px;
`

const WordCloudListContainer = styled.div`
  min-width: 0;
`

const WordCloudListItem = styled.div`
  display: grid;
  grid-template-columns: 64px 1fr 80px;
  align-items: center;
  gap: 8px;

  position: relative;
  padding: 16px;
`

const WordCloudListItemBorderTop = styled.div`
  position: absolute;
  top: 0;

  width: 100%;
  height: 1px;
  background-color: ${({ theme }): string => theme.colors.border.divider};
`

const WordCloudListItemHeader = styled.span`
  font-size: 14px;
  font-weight: 700;
  color: ${({ theme }): string => theme.colors.text.secondary};
`

const WordCloudListItemColumn = styled.span`
  font-size: 14px;
  font-weight: 400;
  color: ${({ theme }): string => theme.colors.text.primary};
`

const Summary = styled.div`
  margin: 24px 0;
`

export default SlideComponent
